/**
 * WorkspaceLinksSection — the arche-side view of Workspace (org Holder)
 * shared asset libraries (ADR-019).
 *
 * Lists the workspaces this arche is linked to with each link's DERIVED
 * validity (the server re-derives it on every read — nothing is cached),
 * warns when a publish would drop shared assets, lets a publisher link the
 * arche to one of their eligible workspaces, and soft-unlink. Hard revoke
 * (which stops live serving immediately) is a workspace-admin action and
 * lives on the Workspace Assets page only.
 */

import * as React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ApiError, type WorkspaceRole } from '@/lib/api';
import {
  useArcheWorkspaceLinks,
  useLinkArcheToWorkspace,
  useUnlinkArcheFromWorkspace,
} from '../hooks/useArcheAssetLibrary';
import { LinkValidityBadge } from './asset-library/link-validity';

interface WorkspaceLinksSectionProps {
  archeId: string;
}

function capitalizeRole(role: WorkspaceRole): string {
  return role.charAt(0).toUpperCase() + role.slice(1);
}

export function WorkspaceLinksSection({ archeId }: WorkspaceLinksSectionProps) {
  const linksQuery = useArcheWorkspaceLinks(archeId);
  const linkMutation = useLinkArcheToWorkspace(archeId);
  const unlinkMutation = useUnlinkArcheFromWorkspace(archeId);
  const [selectedHolderId, setSelectedHolderId] = React.useState('');

  const links = linksQuery.data?.links ?? [];
  const eligible = linksQuery.data?.eligible ?? [];
  const activeLinks = links.filter((l) => l.revokedAt === null);
  const invalidActive = activeLinks.filter((l) => !l.validity.valid);

  // A 403 is the collaborator gate, not a broken page: the links route needs
  // `can_publish` on this arche (linking is a publishing decision, ADR-019).
  // It is the expected answer for an admin who opened a Fias-owned plugin they
  // are not a maintainer of — including right after removing themselves in the
  // Dev Console — so say what to do instead of reporting two load failures.
  if (
    linksQuery.isError &&
    linksQuery.error instanceof ApiError &&
    linksQuery.error.status === 403
  ) {
    return (
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Shared workspace libraries</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            You need Publish access on this arche to see or change its workspace links — linking is
            a publishing decision. Grant yourself Publish in{' '}
            <Link to="/dev-console/plugin-annexation" className="text-primary hover:underline">
              Dev Console → Fias Plugins
            </Link>{' '}
            (admins) or use <span className="font-medium">Become maintainer</span> on the{' '}
            <Link to="/contributor/dashboard" className="text-primary hover:underline">
              contributor dashboard
            </Link>
            , then reload this page.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle>Shared workspace libraries</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Link this arche to a workspace so its published versions can serve the workspace&apos;s
          shared assets. Each publish of this arche pins the workspace&apos;s latest published
          library version; a link only qualifies while a workspace member can publish this arche.{' '}
          <Link to="/contributor/workspaces" className="text-primary hover:underline">
            Manage workspace libraries
          </Link>
        </p>

        {invalidActive.length > 0 && (
          <div
            role="alert"
            className="rounded border border-destructive bg-destructive/5 p-3 text-sm text-destructive"
          >
            {invalidActive.length} shared workspace{invalidActive.length === 1 ? '' : 's'}{' '}
            {invalidActive.length === 1 ? 'is' : 'are'} no longer available to this arche — the next
            publish will drop {invalidActive.length === 1 ? 'its' : 'their'} assets.
          </div>
        )}

        {linksQuery.isLoading ? (
          <div className="text-muted-foreground">Loading…</div>
        ) : linksQuery.isError ? (
          <div className="text-destructive">Could not load workspace links.</div>
        ) : links.length === 0 ? (
          <div className="text-sm text-muted-foreground">Not linked to any workspace.</div>
        ) : (
          <ul className="divide-y rounded border">
            {links.map((link) => (
              <li
                key={link.holderId}
                className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0">
                  <div className="truncate font-medium">{link.displayName}</div>
                  <div className="mt-1">
                    <LinkValidityBadge link={link} />
                  </div>
                </div>
                {/* Unlink is a soft revoke — meaningless on an already-revoked row. */}
                {link.canUnlink && link.revokedAt === null && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => unlinkMutation.mutate(link.holderId)}
                    disabled={unlinkMutation.isPending}
                    aria-label={`Unlink ${link.displayName}`}
                  >
                    Unlink
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
        {unlinkMutation.isError && (
          <div role="alert" className="text-sm text-destructive">
            Could not unlink:{' '}
            {unlinkMutation.error instanceof Error ? unlinkMutation.error.message : 'unknown error'}
          </div>
        )}

        <div className="border-t pt-4">
          <label className="block text-sm font-medium" htmlFor={`link-workspace-${archeId}`}>
            Link workspace
          </label>
          {/*
            The "nothing to link" copy is a statement about the caller's
            workspaces, so it is only true once the query has SUCCEEDED —
            during loading or after an error it would be a lie.
          */}
          {linksQuery.isLoading ? (
            <div className="mt-1 text-sm text-muted-foreground">Loading your workspaces…</div>
          ) : linksQuery.isError ? (
            <div role="alert" className="mt-1 text-sm text-destructive">
              Could not load the workspaces you can link.
            </div>
          ) : !linksQuery.isSuccess ? null : eligible.length === 0 ? (
            <div className="mt-1 text-sm text-muted-foreground">
              No workspace to link. You can link a workspace where you are an owner or admin, or a
              member when the workspace allows members to link their own arches, and whose library
              is enabled.
            </div>
          ) : (
            <div className="mt-1 flex flex-col gap-2 sm:flex-row">
              <select
                id={`link-workspace-${archeId}`}
                className="rounded border border-input bg-background p-2 text-sm"
                value={selectedHolderId}
                onChange={(e) => setSelectedHolderId(e.target.value)}
              >
                <option value="">— choose a workspace —</option>
                {eligible.map((w) => (
                  <option key={w.holderId} value={w.holderId}>
                    {w.displayName} ({capitalizeRole(w.role)})
                  </option>
                ))}
              </select>
              <Button
                onClick={() => {
                  if (!selectedHolderId) return;
                  linkMutation.mutate(selectedHolderId, {
                    onSuccess: () => setSelectedHolderId(''),
                  });
                }}
                disabled={!selectedHolderId || linkMutation.isPending}
              >
                {linkMutation.isPending ? 'Linking…' : 'Link'}
              </Button>
            </div>
          )}
          {linkMutation.isError && (
            <div role="alert" className="mt-2 text-sm text-destructive">
              Could not link:{' '}
              {linkMutation.error instanceof Error ? linkMutation.error.message : 'unknown error'}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
